import { cn } from '../../lib/utils';

export function normalizeWebsiteUrl(website: string): string {
  return website.startsWith('http') ? website : `https://${website}`;
}

function descriptionClampClass(lines: number): string {
  if (lines <= 0) return '';
  if (lines === 1) return 'line-clamp-1';
  if (lines === 2) return 'line-clamp-2';
  if (lines === 3) return 'line-clamp-3';
  return 'line-clamp-4';
}

export interface DetailListItemProps {
  title: string;
  description?: string | null;
  website?: string | null;
  meta?: React.ReactNode;
  trailing?: React.ReactNode;
  onSelect: () => void;
  className?: string;
  descriptionLines?: number;
}

export function DetailListItem({
  title,
  description,
  website,
  meta,
  trailing,
  onSelect,
  className,
  descriptionLines = 4,
}: DetailListItemProps) {
  const clampClass = descriptionClampClass(descriptionLines);

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onSelect}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect();
        }
      }}
      className={cn(
        'group px-4 sm:px-6 py-4 hover:bg-hover transition-colors cursor-pointer border-b border-border last:border-b-0',
        className
      )}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          {website ? (
            <a
              href={normalizeWebsiteUrl(website)}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="text-base font-semibold text-content-text group-hover:text-primary transition-colors leading-snug"
            >
              {title}
            </a>
          ) : (
            <span className="text-base font-semibold text-content-text group-hover:text-primary transition-colors leading-snug">
              {title}
            </span>
          )}

          {description?.trim() && (
            <p
              className={cn(
                'mt-2 text-sm text-content-text-muted leading-relaxed',
                clampClass
              )}
            >
              {description.trim()}
            </p>
          )}

          {meta && (
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-3 text-xs text-content-text-muted">
              {meta}
            </div>
          )}
        </div>

        {trailing && (
          <div
            className="flex-shrink-0 flex items-center gap-2"
            onClick={(e) => e.stopPropagation()}
          >
            {trailing}
          </div>
        )}
      </div>
    </div>
  );
}
